import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

export const luma = definePreset(Aura, {
  primitive: {
    emerald: {
      50: '#e6f7f2',
      100: '#ccefe5',
      200: '#99e0cb',
      300: '#66d1b1',
      400: '#33c197',
      500: '#00b17d',
      600: '#009f70',
      700: '#008c62',
      800: '#007953',
      900: '#006446',
      950: '#004d38'
    },

    green: {
      50: '#e6f5e8',
      100: '#ccebd1',
      200: '#99d7a3',
      300: '#66c376',
      400: '#33af49',
      500: '#009b1c',
      600: '#008b19',
      700: '#007817',
      800: '#006514',
      900: '#005212',
      950: '#003f0f'
    },

    lime: {
      50: '#f2fde6',
      100: '#e5fccc',
      200: '#cbf999',
      300: '#b2f666',
      400: '#98f433',
      500: '#7ef200',
      600: '#71d800',
      700: '#63bd00',
      800: '#56a300',
      900: '#488800',
      950: '#3a6d00'
    },

    red: {
      50: '#fce9e6',
      100: '#f9d3cc',
      200: '#f4a799',
      300: '#ee7b66',
      400: '#e84e33',
      500: '#e22100',
      600: '#cb1e00',
      700: '#b41a00',
      800: '#9d1600',
      900: '#851100',
      950: '#6e0d00'
    },

    orange: {
      50: '#fceee6',
      100: '#f9ddcc',
      200: '#f4bb99',
      300: '#ee9966',
      400: '#e97733',
      500: '#e35500',
      600: '#cc4d00',
      700: '#b44400',
      800: '#9d3c00',
      900: '#853300',
      950: '#6e2b00'
    },

    amber: {
      50: '#fff8e6',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#612a0d'
    },

    yellow: {
      50: '#fffbe6',
      100: '#fff7cc',
      200: '#ffef99',
      300: '#ffe766',
      400: '#ffdf33',
      500: '#ffd700',
      600: '#e6c100',
      700: '#ccab00',
      800: '#b39600',
      900: '#997f00',
      950: '#806500'
    },

    teal: {
      50: '#e6f9f8',
      100: '#ccf3f2',
      200: '#99e6e4',
      300: '#66d9d7',
      400: '#33cccc',
      500: '#00bfbf',
      600: '#00aaaa',
      700: '#009494',
      800: '#007d7d',
      900: '#006666',
      950: '#004f4f'
    },

    cyan: {
      50: '#e6f9fc',
      100: '#ccf3f9',
      200: '#99e6f3',
      300: '#66d9ec',
      400: '#33cce6',
      500: '#00bfdf',
      600: '#00aad1',
      700: '#0095c3',
      800: '#007fb5',
      900: '#006aa7',
      950: '#005592'
    },

    sky: {
      50: '#e6f4fc',
      100: '#ccf0f9',
      200: '#99e1f3',
      300: '#66d2ec',
      400: '#33c3e6',
      500: '#00b4df',
      600: '#00a1cc',
      700: '#008db9',
      800: '#007aa6',
      900: '#006793',
      950: '#005080'
    },

    blue: {
      // Formerly primary
      50: '#e4eef8',
      100: '#c8ddef',
      200: '#a2c3e2',
      300: '#7aa9d5',
      400: '#5590c8',
      500: '#0355A1', // Base color
      600: '#034c91',
      700: '#03427f',
      800: '#02376c',
      900: '#012653',
      950: '#001b3d'
    },

    indigo: {
      50: '#e8eaf9',
      100: '#d0d5f2',
      200: '#a0abe6',
      300: '#7080d9',
      400: '#4056cd',
      500: '#102cc0',
      600: '#0e28ad',
      700: '#0d2399',
      800: '#0b1f85',
      900: '#091a71',
      950: '#061453'
    },

    violet: {
      50: '#f3e6fc',
      100: '#e6ccf9',
      200: '#cc99f3',
      300: '#b366ec',
      400: '#9933e6',
      500: '#8000df',
      600: '#7300c9',
      700: '#6600b3',
      800: '#59009d',
      900: '#4c0086',
      950: '#3d006d'
    },

    purple: {
      50: '#f5e6f9',
      100: '#ebccf3',
      200: '#d799e6',
      300: '#c266d9',
      400: '#ae33cc',
      500: '#9900bf',
      600: '#8900aa',
      700: '#780094',
      800: '#67007f',
      900: '#560069',
      950: '#440054'
    },

    fuchsia: {
      50: '#f9e6f9',
      100: '#f2ccf3',
      200: '#e699e6',
      300: '#d966d9',
      400: '#cc33cc',
      500: '#bf00bf',
      600: '#aa00aa',
      700: '#940094',
      800: '#7f007f',
      900: '#690069',
      950: '#540054'
    },

    pink: {
      50: '#fde6f2',
      100: '#fbcced',
      200: '#f699db',
      300: '#f066c8',
      400: '#eb33b6',
      500: '#e600a3',
      600: '#cf0093',
      700: '#b80083',
      800: '#a20073',
      900: '#8b0063',
      950: '#70004d'
    },

    rose: {
      50: '#fde6ec',
      100: '#fbcdd9',
      200: '#f799b3',
      300: '#f2668c',
      400: '#ee3366',
      500: '#ea003f',
      600: '#d30039',
      700: '#bc0033',
      800: '#a5002d',
      900: '#8e0027',
      950: '#71001f'
    },

    slate: {
      50: '#f1f5f9',
      100: '#e2e8f0',
      200: '#cbd5e1',
      300: '#94a3b8',
      400: '#64748b',
      500: '#475569',
      600: '#334155',
      700: '#1e293b',
      800: '#0f172a',
      900: '#0a1123',
      950: '#070d1a'
    },

    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
      950: '#0d141f'
    },

    zinc: {
      50: '#fafafa',
      100: '#f4f4f5',
      200: '#e4e4e7',
      300: '#d4d4d8',
      400: '#a1a1aa',
      500: '#71717a',
      600: '#52525b',
      700: '#3f3f46',
      800: '#27272a',
      900: '#18181b',
      950: '#0f0f11'
    },

    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
      950: '#101010'
    },

    stone: {
      50: '#fafaf9',
      100: '#f5f5f4',
      200: '#e7e5e4',
      300: '#d6d3d1',
      400: '#a8a29e',
      500: '#78716c',
      600: '#57534e',
      700: '#44403c',
      800: '#292524',
      900: '#1c1917',
      950: '#15130f'
    }
  },
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    },
    colorScheme: {
      light: {
        primary: {
          color: '{blue.500}',
          contrastColor: '{gray.50}',
          hoverColor: '{blue.900}',
          activeColor: '{blue.800}'
        },
        highlight: {
          background: '{blue.950}',
          focusBackground: '{blue.700}',
          color: '{gray.50}',
          focusColor: '{gray.50}'
        },
        text: {
          color: '{gray.700}',
          mutedColor: '{gray.400}',
          hoverMutedColor: '{gray.300}',
          hoverColor: '{gray.600}',
          inverseColor: '{gray.50}'
        }
      },
      dark: {
        primary: {
          color: '{blue.500}',
          contrastColor: '{gray.50}',
          hoverColor: '{blue.700}',
          activeColor: '{blue.600}'
        },
        highlight: {
          background: 'rgba(250, 250, 250, .16)',
          focusBackground: 'rgba(250, 250, 250, .24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        }
      }
    }
  },
  extend: {
    text: {
      contrastColor: '{gray.50}'
    },
    appleGreen: {
      50: '#f1fcf2',
      100: '#dff9e4',
      200: '#c1f1cb',
      300: '#90e5a3',
      400: '#58d073',
      500: '#32b550',
      600: '#28a745',
      700: '#1f7634',
      800: '#1e5d2d',
      900: '#1a4d27',
      950: '#092a12'
    },
    brown: {
      50: '#f3ebe8',
      100: '#e6d7d1',
      200: '#cdb0a3',
      300: '#b58976',
      400: '#9c6248',
      500: '#834b32',
      600: '#76432d',
      700: '#663924',
      800: '#552f1c',
      900: '#442514',
      950: '#351d10'
    },
    amberLight: {
      50: '#ffffea',
      100: '#fffbc5',
      200: '#fff885',
      300: '#ffee46',
      400: '#ffdf1b',
      500: '#ffc107',
      600: '#e29400',
      700: '#bb6902',
      800: '#985108',
      900: '#7c420b',
      950: '#482200'
    },
    easternBlue: {
      50: '#eefcfd',
      100: '#d3f7fa',
      200: '#adeef4',
      300: '#74dfec',
      400: '#34c8dc',
      500: '#17a2b8',
      600: '#1789a3',
      700: '#196e85',
      800: '#1d5b6d',
      900: '#1d4c5c',
      950: '#0d313f'
    },
    redRibbon: {
      50: '#fef2f2',
      100: '#fee6e5',
      200: '#fccfcf',
      300: '#f9a8a8',
      400: '#f57779',
      500: '#ec474f',
      600: '#dc3545',
      700: '#b7192c',
      800: '#99182c',
      900: '#83182c',
      950: '#490812'
    }
  }
});
